import {
  CellDep,
  CellInput,
  CellOutput,
  OutPoint,
  Script,
  Transaction,
  TransactionLike,
} from "@ckb-ccc/core";

import {
  TransactionWithScriptGroups,
  Transaction as TransactionMessage,
  CellDepLike,
  CellInputLike,
  CellOutputLike,
  OutPointLike,
  ScriptLike,
  ScriptGroup,
} from "../api/types/nervosTransactionTypes";
import { DepType } from "@ckb-ccc/core";

function convertOutPoint(op: OutPointLike): OutPoint {
  return new OutPoint(`0x${op.tx_hash.replace(/^0x/, "")}`, BigInt(op.index));
}

function convertScript(s: ScriptLike): Script {
  return new Script(
    `0x${s.code_hash.replace(/^0x/, "")}`,
    s.hash_type,
    `0x${s.args.replace(/^0x/, "")}`,
  );
}

function convertCellDep(dep: CellDepLike): CellDep {
  let type: string = dep.dep_type;
  if (dep.dep_type === "dep_group") {
    type = "depGroup";
  }
  return new CellDep(convertOutPoint(dep.out_point), type as DepType);
}

function convertCellInput(input: CellInputLike): CellInput {
  return new CellInput(
    convertOutPoint(input.previous_output),
    BigInt(input.since),
  );
}

function convertCellOutput(output: CellOutputLike): CellOutput {
  return new CellOutput(
    BigInt(output.capacity),
    convertScript(output.lock),
    output.type ? convertScript(output.type) : undefined,
  );
}

function toPrefixedHexArray(arr: string[]): `0x${string}`[] {
  return arr.map((v) => (v.startsWith("0x") ? v : `0x${v}`)) as `0x${string}`[];
}

export function convertTxToCcc(
  tx: TransactionWithScriptGroups,
): TransactionLike {
  return {
    version: BigInt(tx.tx_view.version),
    cellDeps: tx.tx_view.cell_deps.map(convertCellDep),
    headerDeps: toPrefixedHexArray(tx.tx_view.header_deps),
    inputs: tx.tx_view.inputs.map(convertCellInput),
    outputs: tx.tx_view.outputs.map(convertCellOutput),
    outputsData: toPrefixedHexArray(tx.tx_view.outputs_data),
    witnesses: toPrefixedHexArray(tx.tx_view.witnesses),
  };
}

function fromHexArray(arr: (string | `0x${string}`)[]): string[] {
  return arr.map(ensure0x);
}

function ensure0x(val: string): string {
  if (!val) return "0x";
  return val.startsWith("0x") ? val : `0x${val}`;
}

// Reverse OutPoint
function reverseOutPoint(op: OutPoint): OutPointLike {
  const outPoint = new OutPointLike();
  outPoint.tx_hash = op.txHash.startsWith("0x") ? op.txHash : `0x${op.txHash}`;
  outPoint.index = `0x${BigInt(op.index).toString(16)}`;
  return outPoint;
}

// Reverse Script
function reverseScript(script: Script): ScriptLike {
  const s = new ScriptLike();
  s.code_hash = ensure0x(script.codeHash);
  s.hash_type = script.hashType as any;
  s.args = ensure0x(script.args);
  return s;
}

// Reverse CellDep
function reverseCellDep(dep: CellDep): CellDepLike {
  const cellDep = new CellDepLike();
  cellDep.out_point = reverseOutPoint(dep.outPoint);
  cellDep.dep_type = dep.depType as any;
  return cellDep;
}

// Reverse CellInput
function reverseCellInput(input: CellInput): CellInputLike {
  const i = new CellInputLike();
  i.previous_output = reverseOutPoint(input.previousOutput);
  i.since = `0x${BigInt(input.since).toString(16)}`;
  return i;
}

// Reverse CellOutput
function reverseCellOutput(output: CellOutput): CellOutputLike {
  const o = new CellOutputLike();
  o.capacity = `0x${BigInt(output.capacity).toString(16)}`;
  o.lock = reverseScript(output.lock);
  o.type = output.type ? reverseScript(output.type) : null;
  return o;
}

// Final export
export function convertCccTxToTxWithScriptGroups(
  tx: Transaction,
  scriptGroups: ScriptGroup[] = [],
): TransactionMessage {
  return new TransactionMessage(
    `0x${BigInt(tx.version).toString(16)}`,
    tx.hash(), // keep hash as-is if already `0x...`
    tx.cellDeps.map(reverseCellDep),
    tx.headerDeps.map((h) => (h.startsWith("0x") ? h : `0x${h}`)),
    tx.inputs.map(reverseCellInput),
    tx.outputs.map(reverseCellOutput),
    fromHexArray(tx.outputsData ?? []),
    fromHexArray(tx.witnesses ?? []),
  );
}
